import { PartnerType } from '../integrations/partner';
import { authFetch } from './api';

type PlatformType = PartnerType | 'stripe';

type PlatformApiRequest = {
  workspaceId?: string; // should always be supplied, but we don't enforce it yet.
  credentialId?: string | null; // should always be supplied, but we don't enforce it yet.
  platformType: PlatformType;
  operation: string;
  apiSubset: string;
  pathParams?: Record<string, any>;
  queryParams?: Record<string, any>;
  query?: string;
  data?: any;
};

export async function platformApi(
  request: PlatformApiRequest,
  customHeaders?: HeadersInit,
  isJson?: boolean,
) {
  return authFetch(
    '/platform_api',
    { method: 'POST', body: JSON.stringify(request), headers: customHeaders },
    isJson,
  );
}

// Cache for platform API responses
type CacheEntry = {
  data: any;
  timestamp: number;
  ttl: number;
};

const platformApiCache = new Map<string, CacheEntry>();

// Default TTL of 15 minutes for cached responses
const DEFAULT_CACHE_TTL = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Creates a cache key for a platform API request
 */
function createCacheKey(request: PlatformApiRequest): string {
  // Create a deterministic key from the request parameters
  const keyObject = {
    workspaceId: request.workspaceId,
    credentialId: request.credentialId,
    platformType: request.platformType,
    operation: request.operation,
    apiSubset: request.apiSubset,
    pathParams: request.pathParams,
    queryParams: request.queryParams,
    query: request.query,
    data: request.data,
  };

  return JSON.stringify(keyObject);
}

/**
 * Checks if a cache entry is still valid
 */
function isCacheEntryValid(entry: CacheEntry): boolean {
  return Date.now() - entry.timestamp < entry.ttl;
}

/**
 * Cached version of platformApi that memoizes responses for READ operations
 * Only caches GET-like operations (list*, get*, children*) to avoid caching mutations
 */
// TODO use react-query caching in consuming components instead
export async function cachedPlatformApi(
  request: PlatformApiRequest,
  customHeaders?: HeadersInit,
  isJson?: boolean,
  cacheTtl: number = DEFAULT_CACHE_TTL,
): Promise<any> {
  // Only cache read operations (list, get, children, etc.)
  const isReadOperation =
    request.operation.startsWith('list') ||
    request.operation.startsWith('get') ||
    request.operation.startsWith('children');

  if (!isReadOperation) {
    // For write operations, use the original platformApi without caching
    return platformApi(request, customHeaders, isJson);
  }

  const cacheKey = createCacheKey(request);
  const cachedEntry = platformApiCache.get(cacheKey);

  // Return cached data if it exists and is still valid
  if (cachedEntry && isCacheEntryValid(cachedEntry)) {
    return cachedEntry.data;
  }

  // Fetch fresh data
  const data = await platformApi(request, customHeaders, isJson);

  // Cache the response
  platformApiCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
    ttl: cacheTtl,
  });

  return data;
}

/**
 * Clears the platform API cache
 */
export function clearPlatformApiCache(): void {
  platformApiCache.clear();
}

/**
 * Clears cache entries for a specific platform type and credential
 */
export function clearPlatformApiCacheForCredential(
  platformType: PartnerType,
  credentialId?: string | null,
): void {
  const keysToDelete: string[] = [];

  for (const [key] of platformApiCache.entries()) {
    try {
      const keyObject = JSON.parse(key);
      if (
        keyObject.platformType === platformType &&
        (!credentialId || keyObject.credentialId === credentialId)
      ) {
        keysToDelete.push(key);
      }
    } catch (error) {
      // Invalid key format, skip
      continue;
    }
  }

  keysToDelete.forEach((key) => platformApiCache.delete(key));
}
